import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, TableCell, TextField, Typography, Autocomplete } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { formatDate, formatCurrency, formatPercentage } from '../../utils/formatters';

export const ActionIconButton = ({ children, title, onClick, Icon, disabled, sx = {}, iconSx = {}, ...props }) => (
  <IconButton
    size="small"
    variant="contained"
    onClick={onClick}
    disabled={disabled}
    sx={{
      fontSize: '10px',
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'not-allowed' : 'pointer',
      ...sx
    }}
    title={title}
    {...props}
  >
    <Icon sx={{
      fontSize: '20px',
      ...iconSx
    }} />
  </IconButton>
);

const FormTableCell = ({ children, align = "center", sx = {}, ...props }) => (
  <TableCell
    align={align}
    sx={{
      border: '1px solid #e0e0e0',
      py: 0.5,
      px: 1,
      ...sx
    }}
    {...props}
  >
    {children}
  </TableCell>
);

export const FormTableHeaderCell = ({ children, sx = {}, ...props }) => (
  <FormTableCell
    sx={{
      backgroundColor: '#f5f5f5',
      fontWeight: 'bold',
      borderTop: '2px solid #bdbdbd',
      borderBottom: '2px solid #bdbdbd',
      ...sx
    }}
    {...props}
  >
    {children}
  </FormTableCell>
);

export const FormTableDetailCell = ({ children, sx = {}, ...props }) => (
  <FormTableCell
    sx={{
      backgroundColor: '#fafafa',
      ...sx
    }}
    {...props}
  >
    {children}
  </FormTableCell>
);

export const FormTableLiabilityCell = ({ children, sx = {}, ...props }) => (
  <FormTableCell
    sx={{
      backgroundColor: '#ffebee',
      ...sx
    }}
    {...props}
  >
    {children}
  </FormTableCell>
);

export const FormTableEquityCell = ({ children, sx = {}, ...props }) => (
  <FormTableCell
    sx={{
      backgroundColor: '#e3f2fd',
      fontWeight: 'bold',
      ...sx
    }}
    {...props}
  >
    {children}
  </FormTableCell>
);

export const FormTableTotalCell = ({ children, sx = {}, ...props }) => (
  <FormTableCell
    sx={{
      backgroundColor: '#c8e6c9',
      fontWeight: 'bold',
      borderTop: '2px solid #81c784',
      borderBottom: '2px solid #81c784',
      ...sx
    }}
    {...props}
  >
    {children}
  </FormTableCell>
);

/**
 * Unified NotesField component that supports both account/liability notes and settlement adjustment notes
 *
 * Two usage patterns:
 *
 * Pattern 1 - Account/Liability (with parameters):
 * <NotesField
 *   notes={account.notes}
 *   onEditClick={() => openNotesDialog(account_type, categoryName, accountIndex, liabilityIndex, account.notes)}
 *   disabled={disabled}
 * />
 *
 * Pattern 2 - Settlement Adjustment (simpler):
 * <NotesField
 *   notes={watchedData?.settlement_adjustment?.notes}
 *   onEditClick={() => openSettlementAdjustmentNotesDialog(watchedData?.settlement_adjustment?.notes)}
 *   disabled={savingRecords.has('settlement-adjustment')}
 * />
 */
export const NotesField = ({ notes, onEditClick, disabled }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, width: '100%' }}>
    <Typography
      variant="body2"
      sx={{
        fontStyle: 'italic',
        color: disabled ? '#999' : '#666',
        fontSize: '10px',
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {notes || ''}
    </Typography>
    <ActionIconButton
      onClick={onEditClick}
      disabled={disabled}
      sx={{
        backgroundColor: disabled ? '#ccc' : '#2196f3',
        color: 'white',
        '&:hover': { backgroundColor: disabled ? '#ccc' : '#1976d2' },
        p: 0.25,
        flexShrink: 0
      }}
      title="Edit Notes"
      Icon={EditIcon}
      iconSx={{ fontSize: '12px' }}
    />
  </Box>
);






export const TrackedTextField = ({ field, onBlurHandler, disabled, ...textFieldProps }) => {
  const valueOnFocus = useRef(null);

  const handleFocus = () => {
    valueOnFocus.current = field.value;
    // //console.log('TextField focused, value:', field.value);
  };

  const handleBlur = () => {
    field.onBlur();

    if (onBlurHandler && typeof onBlurHandler === 'function') {
      const hasChanged = field.value !== valueOnFocus.current;

      if (hasChanged) {
        // //console.log('TextField changed from', valueOnFocus.current, 'to', field.value, '- triggering save');
        onBlurHandler(field.value);
      } else {
        //console.log('TextField unchanged, skipping save');
      }
    }
  };

  return (
    <TextField
      {...textFieldProps}
      disabled={disabled}
      value={field.value ?? ''}
      onChange={(e) => field.onChange(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

export const TrackedAutocomplete = ({
  field,
  onBlurHandler,
  onChangeCallback, // New prop for side effects
  disabled,
  options,
  freeSolo = false,
  textFieldProps = {},
  ...autocompleteProps
}) => {
  const valueOnFocus = useRef(null);

  const handleFocus = () => {
    valueOnFocus.current = field.value;
    //console.log('Autocomplete focused, value:', field.value);
  };

  const handleBlur = () => {
    field.onBlur();

    if (onBlurHandler && typeof onBlurHandler === 'function') {
      const hasChanged = field.value !== valueOnFocus.current;

      if (hasChanged) {
        //console.log('Autocomplete changed from', valueOnFocus.current, 'to', field.value, '- triggering save');
        onBlurHandler(field.value);
      } else {
        //console.log('Autocomplete unchanged, skipping save');
      }
    }
  };

  return (
    <Autocomplete
      {...autocompleteProps}
      freeSolo={freeSolo}
      disabled={disabled}
      options={options}
      value={field.value ?? ''}
      onChange={(event, newValue) => {
        field.onChange(newValue || '');
        // Call the side effect callback if provided
        if (onChangeCallback && typeof onChangeCallback === 'function') {
          onChangeCallback(newValue || '');
        }
      }}
      onInputChange={(event, newInputValue) => {
        if (freeSolo) {
          field.onChange(newInputValue);
          // Call the side effect callback if provided
          if (onChangeCallback && typeof onChangeCallback === 'function') {
            onChangeCallback(newInputValue);
          }
        }
      }}
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      )}
    />
  );
};

export const ValuePercentField = ({ field, basis, initialDriver = 'percentage', onBlurHandler, disabled }) => {
  const isInitialized = useRef(false);
  const previousBasis = useRef(null);
  const previousFieldValue = useRef(null);
  const isInternalUpdate = useRef(false);
  const valueOnFocus = useRef(null);
  const [isEditing, setIsEditing] = useState({ value: false, percentage: false });

  useEffect(() => {
    //console.log('=== Effect running ===');
    //console.log('basis:', basis);
    //console.log('previousBasis:', previousBasis.current);
    //console.log('field.value:', field.value);
    //console.log('previousFieldValue:', previousFieldValue.current);
    //console.log('isInitialized:', isInitialized.current);
    //console.log('isInternalUpdate:', isInternalUpdate.current);

    if (isInternalUpdate.current) {
      //console.log('✓ Skipping - this was our own update');
      isInternalUpdate.current = false;
      previousFieldValue.current = field.value;
      return;
    }

    if (!basis && basis !== 0) {
      //console.log('No basis, skipping');
      return;
    }

    const currentData = field.value || {};
    const value = currentData.value;
    const percentage = currentData.percentage;
    const driver = currentData.driver || initialDriver;

    if (!isInitialized.current) {
      //console.log('→ INITIALIZING');
      let updatedData = null;

      // Explicit check: if driver is "percentage" and percentage exists but value is null
      if (driver === 'percentage' && (percentage !== undefined && percentage !== null) && (value === undefined || value === null)) {
        const calculatedValue = basis * percentage;
        const roundedValue = Math.round(calculatedValue * 100) / 100;
        updatedData = {
          value: roundedValue,
          percentage,
          driver: 'percentage'
        };
        //console.log('→ Initialization: driver=percentage, calculated value from percentage:', roundedValue);
      }
      // If value exists but percentage is null
      else if ((value !== undefined && value !== null) && (percentage === undefined || percentage === null)) {
        const calcPercentage = basis !== 0 ? value / basis : 0;
        // Round percentage to 3 decimal places (1 decimal when displayed as 0-100)
        const roundedPercentage = Math.round(calcPercentage * 1000) / 1000;
        updatedData = {
          value,
          percentage: roundedPercentage,
          driver: driver || 'value'
        };
        //console.log('→ Initialization: calculated percentage from value:', roundedPercentage);
      }
      // If both value and percentage exist
      else if ((value !== undefined && value !== null) && (percentage !== undefined && percentage !== null)) {
        updatedData = {
          value,
          percentage,
          driver: currentData.driver || initialDriver
        };
        //console.log('→ Initialization: both value and percentage exist, keeping both');
      }

      if (updatedData) {
        //console.log('→ Initialization update:', updatedData);
        isInternalUpdate.current = true;
        field.onChange(updatedData);
      }

      isInitialized.current = true;
      previousBasis.current = basis;
      previousFieldValue.current = field.value;
      //console.log('=== Initialized ===\n');
      return;
    }

    const basisChanged = previousBasis.current !== null && basis !== previousBasis.current;
    const fieldValueChanged = JSON.stringify(previousFieldValue.current) !== JSON.stringify(field.value);

    //console.log('basisChanged:', basisChanged);
    //console.log('fieldValueChanged:', fieldValueChanged);

    if (basisChanged) {
      //console.log('→ BASIS CHANGED from', previousBasis.current, 'to', basis);
      //console.log('Current state - driver:', driver, 'value:', value, 'percentage:', percentage);

      let updatedData = null;

      if (basis === 0) {
        // Basis is zero, keep existing logic (commented out for now)
      } else {
        if (driver === 'percentage') {
          if (percentage !== undefined && percentage !== null) {
            const newValue = basis * percentage;
            const roundedValue = Math.round(newValue * 100) / 100;
            //console.log('→ Recalculating VALUE: basis', basis, '* percentage', percentage, '=', roundedValue);
            updatedData = {
              value: roundedValue,
              percentage,
              driver
            };
          }
        } else if (driver === 'value') {
          if (value !== undefined && value !== null) {
            const newPercentage = basis !== 0 ? value / basis : 0;
            // Round percentage to 3 decimal places (1 decimal when displayed as 0-100)
            const roundedPercentage = Math.round(newPercentage * 1000) / 1000;
            //console.log('→ Recalculating PERCENTAGE: value', value, '/ basis', basis, '=', roundedPercentage);
            //console.log('→ This will display as:', (roundedPercentage * 100).toFixed(1) + '%');
            updatedData = {
              value,
              percentage: roundedPercentage,
              driver
            };
          }
        }
      }

      if (updatedData) {
        //console.log('→ Updating field with:', updatedData);
        isInternalUpdate.current = true;
        field.onChange(updatedData);
      }

      previousBasis.current = basis;
      previousFieldValue.current = updatedData || field.value;
      //console.log('=== End basis change ===\n');
    } else if (fieldValueChanged && !basisChanged) {
      //console.log('→ Field value changed (user edit), tracking it');

      // Check if we need to calculate a missing value when field changes
      // This handles the case where the form populates the field with percentage but value is null
      if (driver === 'percentage' && (percentage !== undefined && percentage !== null) && (value === undefined || value === null) && basis && basis !== 0) {
        const calculatedValue = basis * percentage;
        const roundedValue = Math.round(calculatedValue * 100) / 100;
        const updatedData = {
          value: roundedValue,
          percentage,
          driver: 'percentage'
        };
        //console.log('→ Field changed: driver=percentage but value is null, calculating value:', roundedValue);
        isInternalUpdate.current = true;
        field.onChange(updatedData);
        previousFieldValue.current = updatedData;
      } else if (driver === 'value' && (value !== undefined && value !== null) && (percentage === undefined || percentage === null) && basis && basis !== 0) {
        const calculatedPercentage = value / basis;
        const roundedPercentage = Math.round(calculatedPercentage * 1000) / 1000;
        const updatedData = {
          value,
          percentage: roundedPercentage,
          driver: 'value'
        };
        //console.log('→ Field changed: driver=value but percentage is null, calculating percentage:', roundedPercentage);
        isInternalUpdate.current = true;
        field.onChange(updatedData);
        previousFieldValue.current = updatedData;
      } else {
        previousFieldValue.current = field.value;
      }
    } else {
      //console.log('No relevant changes\n');
    }

    // CRITICAL: Always check if we need to calculate a missing value/percentage
    // This ensures that even if timing issues occur, we eventually fix the data
    const finalData = field.value || {};
    const finalValue = finalData.value;
    const finalPercentage = finalData.percentage;
    const finalDriver = finalData.driver || initialDriver;

    if (basis && basis !== 0) {
      if (finalDriver === 'percentage' && (finalPercentage !== undefined && finalPercentage !== null) && (finalValue === undefined || finalValue === null)) {
        const calculatedValue = basis * finalPercentage;
        const roundedValue = Math.round(calculatedValue * 100) / 100;
        const fixedData = {
          value: roundedValue,
          percentage: finalPercentage,
          driver: 'percentage'
        };
        //console.log('→ END CHECK: Fixing missing value:', roundedValue);
        isInternalUpdate.current = true;
        field.onChange(fixedData);
        previousFieldValue.current = fixedData;
      } else if (finalDriver === 'value' && (finalValue !== undefined && finalValue !== null) && (finalPercentage === undefined || finalPercentage === null)) {
        const calculatedPercentage = finalValue / basis;
        const roundedPercentage = Math.round(calculatedPercentage * 1000) / 1000;
        const fixedData = {
          value: finalValue,
          percentage: roundedPercentage,
          driver: 'value'
        };
        //console.log('→ END CHECK: Fixing missing percentage:', roundedPercentage);
        isInternalUpdate.current = true;
        field.onChange(fixedData);
        previousFieldValue.current = fixedData;
      }
    }
  }, [basis, field.value]);

  const currentData = field.value || {};
  const value = currentData.value;
  const percentage = currentData.percentage;
  const driver = currentData.driver || initialDriver;

  const handleFocus = (fieldName) => {
    valueOnFocus.current = JSON.stringify(field.value);
    setIsEditing({ ...isEditing, [fieldName]: true });
  };

  const handleValueChange = (e) => {
    const inputValue = e.target.value;

    if (inputValue === '' || inputValue === '-') {
      field.onChange({ value: undefined, percentage, driver: 'percentage' });
    } else {
      const numericValue = parseFloat(inputValue);
      if (!isNaN(numericValue)) {
        // Round value to 2 decimal places
        const roundedValue = Math.round(numericValue * 100) / 100;
        const calculatedPercentage = basis !== 0 ? roundedValue / basis : 0;
        // Round percentage to 3 decimal places (1 decimal when displayed as 0-100)
        const roundedPercentage = Math.round(calculatedPercentage * 1000) / 1000;
        //console.log('User edited VALUE → driver="value"');
        //console.log('  numericValue:', numericValue);
        //console.log('  roundedValue:', roundedValue);
        //console.log('  basis:', basis);
        //console.log('  calculatedPercentage:', calculatedPercentage);
        //console.log('  roundedPercentage:', roundedPercentage);
        //console.log('  display as:', (roundedPercentage * 100).toFixed(1) + '%');

        field.onChange({
          value: roundedValue,
          percentage: roundedPercentage,
          driver: 'value'
        });
      }
    }
  };

  const handlePercentageChange = (e) => {
    const inputValue = e.target.value;

    if (inputValue === '' || inputValue === '-') {
      field.onChange({ value, percentage: undefined, driver: 'value' });
    } else {
      const numericPercentage = parseFloat(inputValue);
      if (!isNaN(numericPercentage) && numericPercentage >= 0) {
        const decimalPercentage = numericPercentage / 100;
        // Round percentage to 3 decimal places (1 decimal when displayed as 0-100)
        const roundedPercentage = Math.round(decimalPercentage * 1000) / 1000;
        const calculatedValue = basis * roundedPercentage;
        // Round calculated value to 2 decimal places
        const roundedValue = Math.round(calculatedValue * 100) / 100;
        //console.log('User edited PERCENTAGE → driver="percentage"');
        //console.log('  numericPercentage:', numericPercentage);
        //console.log('  decimalPercentage:', decimalPercentage);
        //console.log('  roundedPercentage:', roundedPercentage);
        //console.log('  basis:', basis);
        //console.log('  calculatedValue:', calculatedValue);
        //console.log('  roundedValue:', roundedValue);

        field.onChange({
          value: roundedValue,
          percentage: roundedPercentage,
          driver: 'percentage'
        });
      }
    }
  };

  const handleBlur = (fieldName) => {
    setIsEditing({ ...isEditing, [fieldName]: false });
    field.onBlur();

    if (onBlurHandler && typeof onBlurHandler === 'function') {
      const currentValue = JSON.stringify(field.value);
      const hasChanged = currentValue !== valueOnFocus.current;

      if (hasChanged) {
        //console.log('Value changed, triggering save');
        onBlurHandler(field.value);
      } else {
        //console.log('Value unchanged, skipping save');
      }
    }
  };

  // Display helpers
  const displayValue = (val) => {
    if (val === undefined || val === null) return '';
    const rounded = Math.round(val * 100) / 100;
    if (isEditing.value) return rounded;
    return rounded.toFixed(2);
  };

  const displayPercentage = (pct) => {
    if (pct === undefined || pct === null) return '';
    const percentValue = pct * 100;
    const rounded = Math.round(percentValue * 10) / 10; // Round to 1 decimal
    if (isEditing.percentage) return rounded;
    return rounded.toFixed(1); // Display with 1 decimal
  };

  const driverIndicatorStyle = {
    width: '3px',
    height: '20px',
    backgroundColor: '#1976d2',
    borderRadius: '2px',
    opacity: 1
  };

  const inactiveIndicatorStyle = {
    width: '3px',
    height: '20px',
    backgroundColor: 'transparent',
    borderRadius: '2px',
    opacity: 0
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      {/* Value Field */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Box sx={driver === 'value' ? driverIndicatorStyle : inactiveIndicatorStyle} />
        <TextField
          type="number"
          variant="outlined"
          size="small"
          disabled={disabled}
          value={displayValue(value)}
          onChange={handleValueChange}
          onFocus={() => handleFocus('value')}
          onBlur={() => handleBlur('value')}
          inputProps={{ step: 0.01 }}
          placeholder="0.00"
          sx={{
            width: '80px',
            '& .MuiInputBase-input': {
              fontSize: '10px',
              textAlign: 'center',
              padding: '1px',
              backgroundColor: disabled ? '#f5f5f5' : 'white'
            },
            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]': {
              MozAppearance: 'textfield',
            }
          }}
        />
        <Typography variant="caption" sx={{ fontSize: '11px', color: disabled ? '#999' : '#666' }}>
          $
        </Typography>
      </Box>

      {/* Percentage Field */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Box sx={driver === 'percentage' ? driverIndicatorStyle : inactiveIndicatorStyle} />
        <TextField
          type="number"
          variant="outlined"
          size="small"
          disabled={disabled}
          value={displayPercentage(percentage)}
          onChange={handlePercentageChange}
          onFocus={() => handleFocus('percentage')}
          onBlur={() => handleBlur('percentage')}
          inputProps={{ min: 0, step: 0.1 }}
          placeholder="0.0"
          sx={{
            width: '50px',
            '& .MuiInputBase-input': {
              fontSize: '10px',
              textAlign: 'center',
              padding: '1px',
              backgroundColor: disabled ? '#f5f5f5' : 'white'
            },
            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]': {
              MozAppearance: 'textfield',
            }
          }}
        />
        <Typography variant="caption" sx={{ fontSize: '11px', color: disabled ? '#999' : '#666' }}>
          %
        </Typography>
      </Box>
    </Box>
  );
};

export const TrackedNumberField = ({
  field,
  onBlurHandler,
  disabled,
  decimals = 2,
  allowEmpty = true,
  ...textFieldProps
}) => {
  const valueOnFocus = useRef(null);
  const [isEditing, setIsEditing] = useState(false);

  const handleFocus = () => {
    valueOnFocus.current = field.value;
    setIsEditing(true);
    //console.log('NumberField focused, value:', field.value);
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;

    if (inputValue === '' || inputValue === '-') {
      field.onChange(allowEmpty ? null : 0);
    } else {
      const numericValue = parseFloat(inputValue);
      if (!isNaN(numericValue)) {
        // Round to specified decimal places
        const multiplier = Math.pow(10, decimals);
        const rounded = Math.round(numericValue * multiplier) / multiplier;
        field.onChange(rounded);
      }
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    field.onBlur();

    if (onBlurHandler && typeof onBlurHandler === 'function') {
      const hasChanged = field.value !== valueOnFocus.current;

      if (hasChanged) {
        //console.log('NumberField changed from', valueOnFocus.current, 'to', field.value, '- triggering save');
        onBlurHandler(field.value);
      } else {
        //console.log('NumberField unchanged, skipping save');
      }
    }
  };

  const displayValue = () => {
    if (field.value === null || field.value === undefined) return '';
    const multiplier = Math.pow(10, decimals);
    const rounded = Math.round(field.value * multiplier) / multiplier;
    if (isEditing) return rounded;
    return rounded.toFixed(decimals);
  };

  return (
    <TextField
      {...textFieldProps}
      type="number"
      disabled={disabled}
      value={displayValue()}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};